import { CSSResultGroup, TemplateResult, html, nothing } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property } from 'lit/decorators.js';
import { ResizeController } from '../../controllers/resize-controller';
import componentStyles from './tufts-hero.css.lit';
import tuftsHeadingCssLit from '../tufts-heading/tufts-heading.css.lit';
import tuftsSearchBarStyles from '../tufts-search-bar/tufts-search-bar.css.lit';
import '../tufts-card/tufts-card';
import '../tufts-container/tufts-container';
import { ifDefined } from 'lit/directives/if-defined.js';
import { SlotNGController } from '../../controllers/slot-ng-controller';
import '../tufts-hr/tufts-hr';

/**
 * The tuftsHero component
 * @element tufts-hero|
 *
 * @slot title
 * @slot body
 * @slot cta
 * @slot review
 * @slot image
 *
 * @attr pulse-orientation
 * @attr vertical-trigger-width
 */
@customElement('tufts-hero')
export class tuftsHero extends OutlineElement {
  ngSlot = new SlotNGController(this);

  resizeController: ResizeController;

  // The resizeController is initialized here because verticalTriggerWidth is not set yet when constructor runs
  connectedCallback() {
    super.connectedCallback();
    this.resizeController = new ResizeController(this, {
      breakpoints: [this.verticalTriggerWidth],
    });
  }

  static styles: CSSResultGroup = [
    componentStyles,
    tuftsHeadingCssLit,
    // added the styles of the tufts-search-bar so you don't lose the styles
    tuftsSearchBarStyles,
  ];

  @property({ type: String, reflect: true, attribute: 'pulse-orientation' })
  pulsePosition = 'bottom-right';

  /**
   * Width at which layout changes from horizontal to vertical
   */
  @property({
    type: String,
    reflect: true,
    converter: Number,
    attribute: 'vertical-trigger-width',
  })
  verticalTriggerWidth = 860;

  isMobile: boolean;

  @property({
    type: Boolean,
    reflect: true,
    attribute: 'pre-cta-expanded',
  })
  preCtaExpanded = false;

  togglePreCtaExpand() {
    this.preCtaExpanded = !this.preCtaExpanded;
  }

  wideScreen(isMobile: boolean): TemplateResult {
    const isWideScreen: boolean = this.clientWidth > 1960;
    return html`
      <tufts-container
        pulse-orientation="${ifDefined(
          isWideScreen ? undefined : this.pulsePosition
        )}"
        max-pulse-size="${ifDefined(isWideScreen ? undefined : '500px')}"
        container-width="${isWideScreen ? 'wide' : 'full'}"
        padding="0"
      >
        <tufts-container
          pulse-orientation="${ifDefined(
            isWideScreen ? this.pulsePosition : undefined
          )}"
          container-width="${isWideScreen ? 'full' : 'wide'}"
          padding="0"
          background-color="transparent"
        >
          ${this.contentTemplate(isMobile)}
        </tufts-container>
      </tufts-container>
    `;
  }

  contentTemplate(isMobile: boolean): TemplateResult {
    return html`
      <div class="${isMobile ? 'small' : 'desktop'}">
        ${isMobile ? html`${this.ngSlot.conditionalNGSlot('image')}` : null}
        <div class="content">
          ${this.ngSlot.conditionalNGSlot('title', true, 'xxl')}
          ${this.ngSlot.conditionalNGSlot(
            'sub-title',
            true,
            'md special-heading'
          )}
          <div class="cta-wrapper">
            ${this.ngSlot.exist('pre-cta')
              ? html` <button
                  @click="${this.togglePreCtaExpand}"
                  class="pre-cta"
                  aria-controls="pre-cta-drawer"
                  aria-expanded="${this.preCtaExpanded}"
                >
                  Schedule online
                </button>`
              : undefined}
            ${this.ngSlot.conditionalNGSlot('cta')}
          </div>
          <div
            id="pre-cta-drawer"
            class="${this.preCtaExpanded ? 'expanded' : nothing}"
          >
            <slot name="pre-cta"></slot>
          </div>
          ${this.experticeSlotTemplate()} ${this.bodySlotTemplate()}
        </div>
        ${isMobile ? null : html`${this.ngSlot.conditionalNGSlot('image')}`}
      </div>
    `;
  }

  bodySlotTemplate() {
    if (this.ngSlot.exist('body')) {
      return html`
        ${this.ngSlot.conditionalNGSlot('body')}
        <tufts-hr top-margin="0px" bottom-margin="0px"></tufts-hr>
        <slot></slot>
      `;
    }
    return html` <slot></slot>`;
  }
  experticeSlotTemplate() {
    if (this.ngSlot.exist('expertise')) {
      return html`
        ${this.ngSlot.conditionalNGSlot('expertise')}
        <tufts-hr top-margin="0px" bottom-margin="0px"></tufts-hr>
      `;
    }
    return null;
  }

  render(): TemplateResult {
    this.isMobile = this.resizeController.currentBreakpointRange === 0;
    if (this.isMobile) {
      return this.contentTemplate(this.isMobile);
    }
    return this.wideScreen(this.isMobile);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-hero': tuftsHero;
  }
}
